import React from 'react';
import PropTypes from 'prop-types';
import { Select as SelectMui, FormControl, FormHelperText, InputLabel, SelectProps, Theme } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { makeStyles } from '@mui/styles';

export type SelectMenuProps = SelectProps & {
  optionsArray?: Array<{ value: string; label: string }>;
  optionsArrayDisabled?: Array<string>;
  size?: 'sm' | 'md' | 'lg';
  error?: boolean | string;
  helperText?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  defaultOptionText?: string;
  defaultOptionEnabled?: boolean;
  compact?: boolean;
  onChange?: (event: React.ChangeEvent<{ value: unknown }>) => void;
  message?: string;
  input?: React.ReactElement<any, any>;
};

const useStyles = makeStyles((theme: Theme) => ({
  formControl: {
    marginTop: theme.spacing(2),
  },
  label: {
    fontSize: '14px',
    fontWeight: 400,
  },
  select: {
    fontSize: '14px',
    fontWeight: 400,
    backgroundColor: '#ffffff9e',
    '& .MuiSelect-select': {
      fontSize: '14px',
      fontWeight: 400,
      color: '#595959',
    },
  },
  menu: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#595959',
    padding: '8px 16px',
    '&:hover': {
      backgroundColor: 'rgba(0, 108, 250, 0.05)',
    },
    '&.Mui-selected': {
      fontWeight: 400,
      backgroundColor: 'rgba(0, 108, 250, 0.08)',
      '&:hover': {
        backgroundColor: 'rgba(0, 108, 250, 0.08)',
      },
    },
  },
  placeholder: {
    color: '#919eab',
  },
}));

const SelectMenu = ({
  ref,
  label,
  name,
  value,
  required,
  defaultValue,
  disabled,
  optionsArray,
  optionsArrayDisabled,
  defaultOptionText,
  defaultOptionEnabled = false,
  compact = false,
  onChange,
  onBlur,
  onFocus,
  onMouseOver,
  onClose,
  size,
  error,
  message,
  className = '',
  input,
}: SelectMenuProps) => {
  const classes = useStyles();
  const helperText = typeof error === 'string' && error ? error : message;

  const selectElement = (
    <SelectMui
      ref={ref}
      label={compact ? undefined : label}
      variant={compact ? 'standard' : 'outlined'}
      value={value ?? ''}
      required={required}
      disabled={disabled}
      fullWidth={compact}
      displayEmpty={Boolean(defaultOptionText)}
      renderValue={selected => {
        if (!selected && defaultOptionText) {
          return <span className={classes.placeholder}>{defaultOptionText}</span>;
        }

        const selectedOption = optionsArray?.find(option => option.value === selected);
        return selectedOption?.label ?? selected;
      }}
      onChange={onChange}
      onBlur={onBlur}
      onMouseOver={onMouseOver}
      onFocus={onFocus}
      onClose={onClose}
      defaultValue={defaultValue}
      input={input}
      className={compact ? className : classes.select}
      notched={!compact && (!!defaultOptionText || !!value || undefined)}
      disableUnderline={compact}
      MenuProps={{
        elevation: 0,
        PaperProps: {
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            marginTop: '4px',
          },
        },
      }}
      inputProps={{
        name,
        id: `id-select-${name}`,
      }}
    >
      {defaultOptionText && (
        <MenuItem value="" disabled={!defaultOptionEnabled} className={classes.menu}>
          {defaultOptionText}
        </MenuItem>
      )}
      {optionsArray &&
        optionsArray.map((op, idx) => {
          return (
            <MenuItem
              value={op.value}
              key={`option-${idx.toString()}-${op.value}`}
              disabled={optionsArrayDisabled && optionsArrayDisabled.includes(op.value)}
              className={classes.menu}
            >
              {op.label}
            </MenuItem>
          );
        })}
    </SelectMui>
  );

  if (compact) {
    return (
      <>
        {selectElement}
        {helperText && <FormHelperText error={Boolean(error)}>{helperText}</FormHelperText>}
      </>
    );
  }

  return (
    <FormControl
      fullWidth
      variant="outlined"
      className={`custom-select ${classes.formControl} ${className}`.trim()}
      error={Boolean(error)}
      disabled={disabled}
      size={size === 'sm' ? 'small' : undefined}
    >
      {label && (
        <InputLabel className={classes.label} shrink={!!defaultOptionText || !!value} htmlFor={`id-select-${name}`}>
          {label}
        </InputLabel>
      )}
      {selectElement}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

SelectMenu.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  required: PropTypes.bool,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  options: PropTypes.object,
  optionsArrayDisabled: PropTypes.array,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  size: PropTypes.string,
  defaultOptionEnabled: PropTypes.bool,
  error: PropTypes.string,
};

SelectMenu.defaultProps = {
  ref: undefined,
  onFocus: undefined,
  onMouseOver: undefined,
  onClose: undefined,
  message: undefined,
};

export default SelectMenu;
